/**
 * Self-serve organization management.
 *
 * Lists every org the signed-in user belongs to (with their role), lets
 * them create new ones, and — when expanded — manage members + roles for
 * orgs where they have admin or owner standing.
 *
 * Roles surfaced: owner, admin, compliance, member. `compliance` unlocks
 * the dashboards at /compliance/verifications and /compliance/submissions
 * (US-031.4 and US-033.5).
 */

import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from '@app/ui';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { api } from '../../../lib/api';

type Role = 'owner' | 'admin' | 'compliance' | 'member';

const ROLE_LABELS: Record<Role, string> = {
  owner: 'Owner',
  admin: 'Admin',
  compliance: 'Compliance officer',
  member: 'Member',
};

const ROLE_DESCRIPTIONS: Record<Role, string> = {
  owner: 'Full control, including setting other owners.',
  admin: 'Invite and remove members. Set roles up to admin.',
  compliance:
    'Read-only access to the compliance dashboards (NCAA verifications + NIL Go submissions).',
  member: 'Standard member with no admin powers.',
};

export const Route = createFileRoute('/_authenticated/settings/organizations')({
  loader: async ({ context }) => {
    const { getServerApi } = await import('../../../lib/api-server');
    const serverApi = await getServerApi();
    await context.queryClient.ensureQueryData(serverApi.organization.myOrgs.queryOptions());
  },
  component: OrganizationsSettings,
});

function OrganizationsSettings() {
  const { data: orgs } = useSuspenseQuery(api.organization.myOrgs.queryOptions());
  const [creating, setCreating] = useState(false);
  const [expandedOrgId, setExpandedOrgId] = useState<string | null>(null);

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Organizations</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Create an org for your school, collective, or agency — invite teammates and assign roles.
          A <span className="font-medium">compliance officer</span> can view the dashboards at{' '}
          <code className="text-xs bg-muted rounded px-1 py-0.5">/compliance/*</code>.
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-2">
            <CardTitle>Your organizations</CardTitle>
            {!creating && (
              <Button size="sm" onClick={() => setCreating(true)}>
                + Create organization
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {creating && <CreateOrgForm onClose={() => setCreating(false)} />}
          {orgs.length === 0 && !creating && (
            <p className="text-sm text-muted-foreground">
              You aren't in any organizations yet. Create one above.
            </p>
          )}
          {orgs.map((o) => {
            const expanded = expandedOrgId === o.orgId;
            return (
              <div key={o.orgId} className="border rounded-md">
                <button
                  type="button"
                  className="w-full flex items-center justify-between gap-3 p-3 text-left hover:bg-muted/30"
                  onClick={() => setExpandedOrgId(expanded ? null : o.orgId)}
                >
                  <div>
                    <div className="font-medium">{o.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {o.type} · slug: {o.slug}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs rounded-full bg-muted px-2 py-0.5">
                      You: {ROLE_LABELS[o.role as Role]}
                    </span>
                    <span className="text-muted-foreground text-xs">{expanded ? '▾' : '▸'}</span>
                  </div>
                </button>
                {expanded && <OrgMembersPanel orgId={o.orgId} callerRole={o.role as Role} />}
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}

function CreateOrgForm({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [type, setType] = useState<'university' | 'collective' | 'agency' | 'brand' | 'other'>(
    'university',
  );

  const createMutation = useMutation({
    ...api.organization.create.mutationOptions(),
    onSuccess: () => {
      toast.success('Organization created');
      queryClient.invalidateQueries({ queryKey: api.organization.myOrgs.queryKey() });
      onClose();
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <div className="border rounded-md p-3 bg-muted/20 space-y-3">
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Name</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="UC Berkeley Athletics"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Slug (optional)</Label>
          <Input
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="auto-generated from name"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Type</Label>
          <Select value={type} onValueChange={(v) => setType(v as typeof type)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="university">University / school</SelectItem>
              <SelectItem value="collective">Collective</SelectItem>
              <SelectItem value="agency">Agency</SelectItem>
              <SelectItem value="brand">Brand</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onClose} disabled={createMutation.isPending}>
          Cancel
        </Button>
        <Button
          size="sm"
          disabled={createMutation.isPending || name.trim().length < 2}
          onClick={() =>
            createMutation.mutate({
              name: name.trim(),
              slug: slug.trim() || undefined,
              type,
            })
          }
        >
          {createMutation.isPending ? 'Creating…' : 'Create'}
        </Button>
      </div>
    </div>
  );
}

function OrgMembersPanel({ orgId, callerRole }: { orgId: string; callerRole: Role }) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery(
    api.organization.listMembers.queryOptions({ input: { orgId } }),
  );
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<Role>('compliance');

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: api.organization.listMembers.queryKey({ input: { orgId } }),
    });

  // Last invite result — drives the post-invite panel that shows the
  // copyable magic-link URL when email send fails (preview envs without
  // RESEND_API_KEY, rate limits, etc.) and the "Resent" badge when the
  // invitee was a stale stub.
  const [lastInvite, setLastInvite] = useState<{
    email: string;
    role: Role;
    isResend: boolean;
    emailSent: boolean;
    emailError: string | null;
    inviteUrl: string;
  } | null>(null);

  const inviteMutation = useMutation({
    ...api.organization.invite.mutationOptions(),
    onSuccess: (result) => {
      const verb = result.isResend ? 'Resent invitation to' : 'Added';
      toast.success(`${verb} ${inviteEmail} as ${ROLE_LABELS[inviteRole]}`);
      setLastInvite({
        email: inviteEmail,
        role: inviteRole,
        isResend: result.isResend ?? false,
        emailSent: result.emailSent ?? false,
        emailError: result.emailError ?? null,
        inviteUrl: result.inviteUrl,
      });
      setInviteEmail('');
      setInviteOpen(false);
      invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  const removeMutation = useMutation({
    ...api.organization.removeMember.mutationOptions(),
    onSuccess: () => {
      toast.success('Member removed');
      invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  const updateRoleMutation = useMutation({
    ...api.organization.updateMemberRole.mutationOptions(),
    onSuccess: () => {
      toast.success('Role updated');
      invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  const canManage = callerRole === 'owner' || callerRole === 'admin';
  const canChangeRoles = callerRole === 'owner';

  if (isLoading || !data) {
    return (
      <div className="border-t p-3">
        <div className="h-6 bg-muted rounded animate-pulse w-48" />
      </div>
    );
  }

  return (
    <div className="border-t bg-muted/10 p-3 space-y-3">
      {lastInvite && (
        <div
          className={`rounded-md border p-3 text-sm space-y-2 ${
            lastInvite.emailSent
              ? 'border-green-200 bg-green-50 text-green-900'
              : 'border-amber-300 bg-amber-50 text-amber-900'
          }`}
        >
          <div className="flex items-start justify-between gap-2">
            <p className="font-medium">
              {lastInvite.isResend ? 'Resent invitation' : 'Invitation sent'} to {lastInvite.email}{' '}
              <span className="font-normal text-xs text-muted-foreground">
                ({ROLE_LABELS[lastInvite.role]})
              </span>
            </p>
            <button
              type="button"
              className="text-xs text-muted-foreground hover:text-foreground"
              onClick={() => setLastInvite(null)}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
          {!lastInvite.emailSent && (
            <p className="text-xs">
              We couldn't send the email automatically{lastInvite.emailError ? ` (${lastInvite.emailError})` : ''}.
              Copy the one-click sign-in link below and share it manually.
            </p>
          )}
          <div className="flex gap-2 items-center">
            <code className="text-xs bg-background border rounded px-2 py-1 truncate flex-1">
              {lastInvite.inviteUrl}
            </code>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                try {
                  navigator.clipboard.writeText(lastInvite.inviteUrl);
                  toast.success('Link copied to clipboard');
                } catch {
                  toast.error('Copy failed — select the link manually.');
                }
              }}
            >
              Copy link
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Link expires in 7 days · single-use · signs the invitee in directly when clicked.
          </p>
        </div>
      )}

      <div className="flex items-center justify-between">
        <p className="text-sm font-medium">Members ({data.members.length})</p>
        {canManage && !inviteOpen && (
          <Button variant="outline" size="sm" onClick={() => setInviteOpen(true)}>
            + Add member
          </Button>
        )}
      </div>

      {inviteOpen && (
        <div className="border rounded-md p-3 bg-background space-y-2">
          <p className="text-xs text-muted-foreground">
            Adds an existing NilBoard user to this org. They must already have an account — ask them
            to sign up first if not.
          </p>
          <div className="grid sm:grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Email</Label>
              <Input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="teammate@school.edu"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Role</Label>
              <Select value={inviteRole} onValueChange={(v) => setInviteRole(v as Role)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="compliance">Compliance officer</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  {callerRole === 'owner' && <SelectItem value="owner">Owner</SelectItem>}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">{ROLE_DESCRIPTIONS[inviteRole]}</p>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setInviteOpen(false);
                setInviteEmail('');
              }}
              disabled={inviteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={inviteMutation.isPending || !inviteEmail.includes('@')}
              onClick={() =>
                inviteMutation.mutate({
                  orgId,
                  email: inviteEmail.trim(),
                  role: inviteRole,
                })
              }
            >
              {inviteMutation.isPending ? 'Adding…' : 'Add'}
            </Button>
          </div>
        </div>
      )}

      <ul className="space-y-2">
        {data.members.map((m) => {
          const isSelf = m.userId === (data.members.find((x) => x.role === 'owner')?.userId ?? '');
          void isSelf;
          return (
            <li key={m.memberId} className="flex items-center justify-between gap-3 border rounded p-2 bg-background">
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{m.name}</div>
                <div className="text-xs text-muted-foreground truncate">{m.email}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {canChangeRoles ? (
                  <Select
                    value={m.role}
                    onValueChange={(v) =>
                      updateRoleMutation.mutate({ orgId, userId: m.userId, role: v as Role })
                    }
                  >
                    <SelectTrigger className="h-8 w-40 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="owner">Owner</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="compliance">Compliance officer</SelectItem>
                      <SelectItem value="member">Member</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <span className="text-xs rounded-full bg-muted px-2 py-0.5">
                    {ROLE_LABELS[m.role as Role]}
                  </span>
                )}
                {canManage && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeMutation.mutate({ orgId, userId: m.userId })}
                    disabled={removeMutation.isPending}
                    className="text-destructive hover:text-destructive"
                  >
                    Remove
                  </Button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
